import produce from 'immer';
import { camelCase, isString, startCase } from 'lodash';
import { get, has, identity, isEmpty, keyBy, mapValues, omit, set } from 'lodash/fp';

import { Asset, AssetType } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { STYLEABLE_COMPONENTS } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-constants';
import {
  COMPONENT_FIELD_DEFAULTS,
  COMPONENT_SCHEMA,
  ComponentSchemaValues,
  EDITOR_COMPONENTS_SCHEMA,
  isSupportedComponentConfigLibraryType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/constants';
import {
  ComponentSetting,
  ComponentSettingConfig,
  ComponentSettingImage,
  ComponentSettingType,
  ComponentTranslationsSchema,
  EditorComponentSchema,
  SelectedComponentAndStep,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import {
  ScreenItemConfig,
  TranslationKeys,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/types';
import {
  uiStepAllComponentConfigs,
  uiStepAllComponents,
  uiStepComponentsRecursive,
} from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import { serializeTheme } from 'dashboard/lib/theme';
import {
  InquiryTemplateVersionCombinedStep,
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepUi,
  isCombinedStepConfig,
  isUiStepConfig,
  LocalizationMap,
  LocalizationPageKey,
  LocalizationValue,
} from 'dashboard/models/inquiry-template-version';
import { ThemeAttributes } from 'dashboard/models/theme';
import { GOV_ID_OPTION_IMAGE_ASSETS } from 'inquiry/components/ImageLocal/ImageLocal';
import {
  GOVERNMENT_ID_DISPLAY_STRINGS,
  GovernmentIdClass,
  isGovernmentIdClass,
} from 'lib/constants/governmentIds';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import { FieldSchema } from 'lib/fields';
import { StyleValueColorConfig } from 'lib/inquiryStyles';
import { ApiModel } from 'lib/interfaces/api';
import DeviceHandoffOption from 'lib/interfaces/DeviceHandoffOption';
import {
  CombinedStepClientData,
  isCombinedStep,
  isUiStep,
  StepGovernmentIdConfig,
  StepPreview,
  StepUi,
  UiStepType,
} from 'lib/interfaces/StepsConfig';
import {
  FieldKey,
  ImageHostedComponentConfig,
  ImageKey,
  ImageLocalComponentConfig,
  ImageLocalIconStyleKeys,
  isImageComponentConfig,
  isImageHostedComponentConfig,
  isStepperComponentConfig,
  isUiFormFieldComponentConfig,
  isUiFormMultiFieldComponentConfig,
  isUiParentComponent,
  isUiParentComponentConfig,
  MultiFieldKey,
  StepperComponentConfig,
  StepperStepConfig,
  Translation,
  UiComponent,
  UiComponentConfig,
  UiComponentConfigType,
  UiFormComponentConfig,
  UiImageComponentConfig,
  UiParentComponent,
  UiParentComponentConfig,
  UiTextComponent,
  UiTextComponentConfig,
} from 'lib/interfaces/ui-components';
import { captureMessage } from 'lib/reporting';
import { assertNever } from 'lib/utils/types';

/**
 * Helper to retrieve the live updated component config data
 * from steps using the selectedComponentAndStep object in screen editor.
 *
 * This is needed because selectedComponentAndStep is set after user interactions
 * (selected step, component, etc) and does not always contain the most up to date form
 * values.
 */
export const getCurrentComponent = (
  steps: InquiryTemplateVersionStep[],
  selectedComponentAndStep: SelectedComponentAndStep,
): UiComponentConfig | undefined => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const { step, component } = selectedComponentAndStep;
  const currentStep: InquiryTemplateVersionStepUi | undefined = steps
    .filter(isUiStepConfig)
    .find((currStep) => currStep.name === step.name);
  return uiStepAllComponentConfigs(currentStep).find(
    (currentComponent) => currentComponent.name === component.name,
  );
};

/**
 * Returns all keys from a component's config which reference an Inquiry field
 */
export function getComponentFieldKeys<C extends UiFormComponentConfig>(
  componentConfig: C,
): Array<FieldKey<C>> {
  if (isUiFormMultiFieldComponentConfig(componentConfig)) {
    return Object.keys(componentConfig.config).filter((key): key is MultiFieldKey<C> =>
      key.startsWith('field-key-'),
    );
  } else if (isUiFormFieldComponentConfig(componentConfig)) {
    return ['field'] as Array<FieldKey<C>>;
  } else {
    return [];
  }
}

/**
 * Helper for creating fields for newly dropped field components.
 * Retrieves field default values from server schema and sets field key value
 * to match the unique value generated for the component config.
 */
export const createFieldSchema = <C extends UiFormComponentConfig>({
  config,
  fieldAttributeKey,
  fieldKey,
}: {
  config: C;
  fieldAttributeKey?: FieldKey<C>;
  fieldKey?: string;
}): FieldSchema => {
  const fieldSchemaBase: FieldSchema = isUiFormMultiFieldComponentConfig(config)
    ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      COMPONENT_FIELD_DEFAULTS[config.type][fieldAttributeKey].config
    : COMPONENT_FIELD_DEFAULTS[config.type];
  return {
    ...fieldSchemaBase,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    key: fieldKey,
    label: startCase(fieldKey),
  };
};

enum GovernmentIdStepKey {
  SelectPage = 'select-page',
}

const GovernmentIdSkippedPageReasons: Partial<Record<LocalizationPageKey, string>> = {
  [GovernmentIdStepKey.SelectPage]:
    'This screen is skipped since there is only one ID type enabled',
};

export const SKIPPED_PAGE_REASONS: Partial<
  Record<UiStepType, typeof GovernmentIdSkippedPageReasons>
> = {
  [UiStepType.GovernmentId]: GovernmentIdSkippedPageReasons,
};

export type DeviceHandoffPageKey =
  | 'device-handoff-prompt-page'
  | 'device-handoff-started-page'
  | 'device-handoff-email-prompt-page'
  | 'device-handoff-email-sent-page'
  | 'device-handoff-sms-prompt-page'
  | 'device-handoff-sms-sent-page';

export const isDeviceHandoffPageKey = (key: string | undefined): key is DeviceHandoffPageKey =>
  key != null &&
  [
    'device-handoff-prompt-page',
    'device-handoff-started-page',
    'device-handoff-email-prompt-page',
    'device-handoff-email-sent-page',
    'device-handoff-sms-prompt-page',
    'device-handoff-sms-sent-page',
  ].includes(key);

export const shouldShowGovernmentIdSelectView = (config: StepGovernmentIdConfig) => {
  return config.idclasses.length > 1 || config['enabled-skip-unknown'];
};

const getCombinedStepTypeLabel = (
  combinedStepType: InquiryTemplateVersionCombinedStep['type'],
): string => {
  switch (combinedStepType) {
    case UiStepType.Document:
      return 'Document';
    case UiStepType.GovernmentId:
      return 'ID';
    case UiStepType.Selfie:
      return 'Selfie';
    case UiStepType.SelfieReflection:
      return 'Selfie Reflection';
  }
};

export const getCombinedStepItemDisplayName = (
  step: InquiryTemplateVersionCombinedStep,
  pageDisplayName: string,
): string => {
  const nameDisplay = step['name-display'];
  const prefixName =
    nameDisplay != null && nameDisplay.trim().length > 0
      ? nameDisplay
      : getCombinedStepTypeLabel(step.type);
  return `${prefixName} ${pageDisplayName}`;
};

/**
 * @param step a combined step
 * @returns a list of relevant screen item configs (calculated from the step type and step configs)
 */
export const getCombinedStepScreenItemConfigs = (
  step: InquiryTemplateVersionCombinedStep,
): Omit<ScreenItemConfig, 'key' | 'stepToken'>[] => {
  switch (step.type) {
    case UiStepType.Document: {
      return [
        {
          pageKeys: ['prompt-page'],
          displayName: 'prompt',
        },
        {
          pageKeys: ['capture-page'],
          displayName: 'camera capture',
        },
        {
          pageKeys: ['check-page'],
          displayName: 'review',
        },
        {
          pageKeys: ['pending-page'],
          displayName: 'processing',
        },
      ];
    }
    case UiStepType.GovernmentId: {
      const pages: Omit<ScreenItemConfig, 'key' | 'stepToken'>[] = [];
      const isAutoclassificationEnabled = step.config['auto-classification-config']?.['is-enabled'];

      if (!isAutoclassificationEnabled) {
        pages.push({
          pageKeys: ['select-page'],
          displayName: 'select',
        });
      }

      pages.push(
        {
          pageKeys: ['prompt-page'],
          displayName: 'prompt',
        },
        {
          pageKeys: ['capture-page'],
          displayName: 'camera capture',
        },
        {
          pageKeys: ['check-page'],
          displayName: 'review',
        },
      );

      if (isAutoclassificationEnabled) {
        pages.push(
          {
            pageKeys: ['auto-classification-page'],
            pageIdentifier: 'auto-classification-failure-page',
            displayName: 'autoclassification failure',
          },
          {
            pageKeys: ['auto-classification-page'],
            pageIdentifier: 'auto-classification-select-page',
            displayName: 'autoclassification select',
          },
        );
      }

      pages.push({
        pageKeys: ['pending-page'],
        displayName: 'processing',
      });
      return pages;
    }
    case UiStepType.Selfie: {
      return [
        {
          pageKeys: ['prompt-page'],
          displayName: 'prompt',
        },
        {
          pageKeys: ['capture-page', 'pending-page'],
          displayName: 'camera capture',
        },
      ];
    }
    case UiStepType.SelfieReflection: {
      return [
        {
          pageKeys: ['prompt-page'],
          displayName: 'prompt',
        },
        {
          pageKeys: ['capture-page', 'pending-page'],
          displayName: 'camera capture',
        },
      ];
    }
  }
};

export const getMaxDeviceHandoffOptions = (
  steps: InquiryTemplateVersionStep[],
): DeviceHandoffOption[] =>
  Array.from(
    new Set(
      steps
        .filter((step): step is InquiryTemplateVersionCombinedStep => {
          if (isCombinedStepConfig(step)) {
            switch (step.type) {
              case UiStepType.Document:
              case UiStepType.GovernmentId:
              case UiStepType.Selfie:
              case UiStepType.SelfieReflection:
                return [
                  ...step.config['enabled-capture-options-desktop'],
                  ...step.config['enabled-capture-options-mobile'],
                ].includes('device_handoff');
              default:
                assertNever(step);
            }
          }
          return false;
        })
        .flatMap((step) => step.config['device-handoff-options']),
    ),
  );

export const getDeviceHandoffGroupConfig = (
  steps: InquiryTemplateVersionStep[],
): ScreenItemConfig[] => {
  const deviceHandoffOptions: DeviceHandoffOption[] = getMaxDeviceHandoffOptions(steps);
  if (deviceHandoffOptions.length === 0) return [];
  return [
    {
      key: 'device-handoff-prompt-page',
      stepToken: 'device-handoff-prompt-page',
      displayName: 'Device handoff',
      items: [
        {
          key: 'device-handoff-prompt-page',
          stepToken: 'device-handoff-prompt-page',
          displayName: 'Handoff type select',
        },
        {
          key: 'device-handoff-started-page',
          stepToken: 'device-handoff-started-page',
          displayName: 'Handoff started',
        },
        ...deviceHandoffOptions
          // QR is filtered because it is shown directly on the prompt page
          .filter((option) => option !== DeviceHandoffOption.Qr)
          .flatMap((option: Exclude<DeviceHandoffOption, DeviceHandoffOption.Qr>) => {
            switch (option) {
              case DeviceHandoffOption.Email:
                return [
                  {
                    key: 'device-handoff-email-prompt-page',
                    stepToken: 'device-handoff-email-prompt-page',
                    displayName: 'Email',
                  },
                  {
                    key: 'device-handoff-email-sent-page',
                    stepToken: 'device-handoff-email-sent-page',
                    displayName: 'Email sent',
                  },
                ];
              case DeviceHandoffOption.Sms:
                return [
                  {
                    key: 'device-handoff-sms-prompt-page',
                    stepToken: 'device-handoff-sms-prompt-page',
                    displayName: 'SMS',
                  },
                  {
                    key: 'device-handoff-sms-sent-page',
                    stepToken: 'device-handoff-sms-sent-page',
                    displayName: 'SMS sent',
                  },
                ];
              default:
                assertNever(option);
            }
          }),
      ],
    },
  ];
};

export const mapToNavItemConfig = (
  steps: InquiryTemplateVersionStep[],
  useGovIdRedesign = false,
  stepsPreview?: StepPreview[],
): ScreenItemConfig[] => {
  return (
    steps
      // omit non-UI step type
      .filter((step) => step.type !== UiStepType.Complete && step.type !== UiStepType.Loop)
      .flatMap((step) => {
        switch (step.type) {
          case UiStepType.Document: {
            return getCombinedStepScreenItemConfigs(step).map((config) => {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              const key = `${step.name}-${config.pageKeys[0]}`;
              return {
                ...config,
                key,
                displayName: getCombinedStepItemDisplayName(step, config.displayName),
                stepToken: step.name,
              };
            });
          }
          case UiStepType.VerifyWithPersona:
            return [
              {
                key: `${step.name}-passkey-authentication-page`,
                stepToken: 'no-configs',
                displayName: 'Verify with Persona Passkey Authentication',
              },
              {
                key: `${step.name}-success-page`,
                stepToken: 'no-configs',
                displayName: 'Verify with Persona Success',
              },
            ];
          case UiStepType.GovernmentId: {
            return getCombinedStepScreenItemConfigs(step).map((config) => {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              let key = `${step.name}-${config.pageIdentifier || config.pageKeys[0]}`;
              const govConfig = stepsPreview?.find((step) => step.type === 'government_id');
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              if (['capture-page', 'check-page'].includes(config.pageKeys[0]) && useGovIdRedesign) {
                key = `${key}-redesign`;
              }
              return {
                ...config,
                key,
                displayName: getCombinedStepItemDisplayName(step, config.displayName),
                stepToken: step.name,
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                ...(['select-page'].includes(config.pageKeys[0]) &&
                  govConfig &&
                  !shouldShowGovernmentIdSelectView(govConfig.config as StepGovernmentIdConfig) && {
                    skip: true,
                  }),
              };
            });
          }
          case UiStepType.Selfie: {
            return getCombinedStepScreenItemConfigs(step).map((config) => {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              const key = `${step.name}-${config.pageKeys[0]}`;
              return {
                ...config,
                key,
                displayName: getCombinedStepItemDisplayName(step, config.displayName),
                stepToken: step.name,
              };
            });
          }
          case UiStepType.SelfieReflection: {
            return getCombinedStepScreenItemConfigs(step).map((config) => {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              const key = `${step.name}-${config.pageKeys[0]}`;
              return {
                ...config,
                key,
                displayName: getCombinedStepItemDisplayName(step, config.displayName),
                stepToken: step.name,
              };
            });
          }
          case UiStepType.Integration:
          case UiStepType.Ui:
            return {
              key: step.name,
              stepToken: step.name,
              displayName: getDisplayNameForStep(step),
            };
          default:
            assertNever(step);
        }
      })
      .concat(getDeviceHandoffGroupConfig(steps))
  );
};

// TODO (eric): this is janky & temp.. replace once we use display_name broadly for steps
export function getNameForStep(
  step: InquiryTemplateVersionStep | undefined,
  getReadableName: (s: string) => string | undefined = identity,
): string {
  if (step == null) {
    return '';
  }
  const { name, 'name-display': nameDisplay } = step;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return nameDisplay ?? getReadableName(name);
}

export const getDisplayNameForStep = (step: InquiryTemplateVersionStep | undefined): string =>
  getNameForStep(step, getHumanReadableStepName);

export function getHumanReadableStepName(name: GovernmentIdClass | string): string {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return isGovernmentIdClass(name)
    ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      GOVERNMENT_ID_DISPLAY_STRINGS[name]
    : startCase(camelCase(name))
        .replace(' Id ', ' ID ')
        .replace('Sms', 'SMS')
        .replace('Btn', 'Button')
        .replace('Us', 'US')
        .replace('Qr', 'QR');
}

/**
 * Modify translation keys from itv step config, so they can be used as keys for the steps preview
 * title-translations -> title
 * title-other-translations -> title-other
 */
function trimTranslationKeyToClientKey(key: string): string {
  return key
    .split('-')
    .filter((s) => s !== 'translations')
    .join('-');
}

/**
 * Convert configStringKey to the previewMap key, if it maps to a known
 * key
 * @param configStringKey
 * @param previewMap - localization map form stepsPreview
 * @returns
 */
export function getClientLocalizationKey(
  configStringKey: TranslationKeys<LocalizationMap>,
  previewMap: Record<string, string>,
): string | undefined {
  // Converts then implicitly drops keys that don't map to the stepsPreview, so e.g.
  // title_default_value -> title_default_value -> null
  // title_i18n_fallback_key -> title_i18n_fallback_key -> null
  // title_translations -> title -> previewMap[title]
  const clientKey = trimTranslationKeyToClientKey(configStringKey as string);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return Object.prototype.hasOwnProperty.call(previewMap, clientKey) ? clientKey : null;
}

function getTranslationText(
  translation: Translation[],
  locale: InquiryIso6391LanguageCode,
): string | string[] | undefined {
  const index = translation.findIndex((val) => val.locale === locale);
  return translation[index]?.value;
}

function englishTranslationDefaults(text: string): Translation[] {
  return [
    {
      locale: 'en-US',
      value: text,
    },
  ];
}

/**
 * helper for filling in missing translations with english defaults.
 * input components will have their placeholder and label translations set if present.
 * text components will have their text translations set if present.
 */
export function setTranslationDefaults<T extends UiTextComponentConfig, P extends UiTextComponent>(
  component: T,
  updatedComponentText: P['attributes'],
): UiTextComponentConfig {
  component.config = Object.keys(component.config)
    .filter((key) => key.endsWith('-translations'))
    .reduce((componentConfig, translationKey) => {
      const componentKey = translationKey.replace('-translations', '');
      if (
        componentKey in updatedComponentText &&
        isString(get(componentKey, updatedComponentText)) &&
        isEmpty(get(translationKey, componentConfig))
      ) {
        return {
          ...componentConfig,
          [translationKey]: englishTranslationDefaults(get(componentKey, updatedComponentText)),
        };
      } else {
        return componentConfig;
      }
    }, component.config);

  return component;
}

function hasTranslationOverride(
  translations: LocalizationValue,
  locale: InquiryIso6391LanguageCode,
): boolean {
  return (
    Array.isArray(translations) && translations.some((translation) => translation.locale === locale)
  );
}

function getUpdatedLocalizationText(
  config: LocalizationMap,
  preview: Record<string, string>,
  locale: InquiryIso6391LanguageCode,
): Record<string, string | string[]> {
  return (
    Object.entries(config)

      // Drop translation keys that are not *_translations, e.g.
      // title_translations -> keep
      // title_default_value -> drop
      // title_i18n_fallback_key -> drop
      .filter(([key, _]) => key.endsWith('translations'))

      // treat null/empty translation as default - require blank character for empty string override
      .filter(([_, value]) => hasTranslationOverride(value, locale))
      .reduce(
        (attrs, [key, value]) => ({
          ...attrs,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          [getClientLocalizationKey(key, preview)]: getTranslationText(value, locale),
        }),
        preview,
      )
  );
}

function isConfigSetting<ComponentConfig extends UiComponentConfig>(
  componentSetting: ComponentSetting<ComponentConfig>,
): componentSetting is ComponentSettingConfig<ComponentConfig> {
  switch (componentSetting.type) {
    case ComponentSettingType.Config:
      return true;
    default:
      return false;
  }
}

export function getUpdatedComponentText<C extends UiTextComponentConfig, P extends UiTextComponent>(
  componentConfig: C,
  preview: P,
  editorComponentSchema: EditorComponentSchema<C>,
  locale: InquiryIso6391LanguageCode,
): P['attributes'] {
  const updatedComponent = produce(preview, (draftPreview) => {
    editorComponentSchema.properties.translations
      .filter((translationSchema: ComponentTranslationsSchema<C>) =>
        hasTranslationOverride(
          componentConfig.config[
            translationSchema['custom-translations-key'] as keyof UiTextComponentConfig['config']
          ],
          locale,
        ),
      )
      .map((translationSchema) => {
        draftPreview.attributes[
          translationSchema['client-key'] as keyof UiTextComponent['attributes']
        ] = getTranslationText(
          componentConfig.config[
            translationSchema['custom-translations-key'] as keyof UiTextComponentConfig['config']
          ] as Translation[],
          locale,
        ) as string;
      });

    const unnestedComponents = uiStepComponentsRecursive([draftPreview]);
    // for every NESTED translations schema, search within the virtual component for the child component that
    // matches the name found in 'component-preview-name'. If found, update that component preview with
    // the corresopnding translation
    editorComponentSchema.properties.settings.map((setting) => {
      if (isConfigSetting(setting) && isStepperComponentConfig(componentConfig)) {
        (
          componentConfig.config[
            setting['client-key'] as keyof StepperComponentConfig['config']
          ] as StepperStepConfig[]
        ).map((nestedcomponentConfig, index) => {
          setting.translations
            .filter((translationSchema) =>
              hasTranslationOverride(
                nestedcomponentConfig[translationSchema['custom-translations-key']],
                locale,
              ),
            )
            .map((translationSchema) => {
              const translationComponentPreview = unnestedComponents.find(
                (component) =>
                  component.name ===
                  translationSchema['component-preview-name']?.replace('index', index.toString()),
              );
              if (translationComponentPreview) {
                (translationComponentPreview as UiTextComponent).attributes[
                  translationSchema['client-key'] as keyof UiTextComponent['attributes']
                ] = getTranslationText(
                  nestedcomponentConfig[translationSchema['custom-translations-key']] || [],
                  locale,
                ) as string;
              }
            });
        });
      }
    });
  });

  return updatedComponent.attributes;
}

// currently used only for safe typing
const mergeCombinedStepPreview = <T extends CombinedStepClientData>(
  stepPreview: T,
  values: Partial<T['config']['localizations']>,
): T => {
  return {
    ...stepPreview,
    config: {
      ...stepPreview.config,
      localizations: values,
    },
  };
};

const stateAttributes = ['disabled', 'errored', 'focused', 'active'];

const buildStylePath = (styleKey: string) => {
  const stateStylesIndices = styleKey.split('--');
  const baseStyleKey = stateStylesIndices.at(0);
  const stateKeyIndex = stateStylesIndices.findIndex((index) => stateAttributes.includes(index));

  if (stateKeyIndex === -1) {
    // we're styling an element attribute on the base level
    return [...stateStylesIndices, 'base'];
  } else if (stateKeyIndex === 1) {
    // we're styling an element state
    return [baseStyleKey, 'base', stateStylesIndices.at(1)];
  } else {
    // we're styling an element attribute state
    return [...stateStylesIndices];
  }
};

type StyleVariant = { [k: string]: any; namespace: string };
type Style = { [k: string]: any };

export const mergeStyles = <C extends Style, T extends StyleVariant[]>(
  baseStyles: C,
  configStyleVariants: T,
): C => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  if (configStyleVariants == null && baseStyles == null) return null;
  if (configStyleVariants == null) return baseStyles;

  let updatedStyles = produce(baseStyles, (draftComponentStyles) => draftComponentStyles);

  // Note (Evan): At the time of this development, only the base namespace is supported on
  // client. A server call determines which namespaced styles are represented in the
  // preview's `styles` data, which defaults to 'base'
  const baseStyleVariant = configStyleVariants?.find(
    (styleVariant) => styleVariant.namespace === 'base',
  );

  // early exit for style-variants that do not have a base variant set
  // (for inquiry templates and components configured pre-SST)
  if (baseStyleVariant == null) {
    return baseStyles;
  }

  const { namespace, ...rest } = baseStyleVariant;

  Object.keys(rest).forEach((styleKey) => {
    const currentStyleVariant = (baseStyleVariant as Record<string, any>)[styleKey];
    const isVariantArrayType = Array.isArray(currentStyleVariant);

    // If there's no style variant set,
    // or if the variant is a box-model type with null values set,
    // we don't want to override what's set in styles
    if (
      currentStyleVariant == null ||
      (typeof currentStyleVariant != 'string' &&
        !isVariantArrayType &&
        Object.keys(currentStyleVariant)
          .filter((key) => key !== 'unit')
          .some((key) =>
            key === 'value'
              ? currentStyleVariant[key] == null
              : currentStyleVariant[key]?.value == null,
          ))
    ) {
      return;
    }

    // For child-sizes and other style types that are arrays:
    // if style variant is an empty array, default to the preview style
    if (isVariantArrayType && currentStyleVariant.length === 0) {
      return;
    }

    let stylePath = [styleKey, 'base', 'base'];
    // Note (Evan): `--` within a style name denotes a "state-based" or "element-based" style
    // state-based styles are usually modeled after ui states (active, focused, selected)
    // element-based styles are for components comprised of different stylable elements
    if (styleKey.includes('--')) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      stylePath = buildStylePath(styleKey);
    }

    updatedStyles = set(stylePath, currentStyleVariant, updatedStyles);
  });

  return updatedStyles;
};

const createStyledComponentPreview = <
  C extends UiComponent,
  T extends UiComponentConfig['style-variants'],
>(
  component: C,
  componentConfigStyleVariants?: T | null,
): C => ({
  ...component,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  ...(mergeStyles(component.styles, componentConfigStyleVariants) != null && {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    styles: mergeStyles(component.styles, componentConfigStyleVariants),
  }),
});

const mergeComponentPreview = <T extends UiTextComponent | UiParentComponent>(
  component: T,
  values: Partial<T['attributes']>,
): T => {
  const resourceAttributesUnfiltered = {
    ...(component && component.attributes),
    ...values,
  };

  const componentAttributes = mapValues((value) => {
    return value === 0 || value ? value : null;
  }, resourceAttributesUnfiltered);

  const mergedPreview = {
    ...component,
    attributes: componentAttributes,
  };

  return mergedPreview;
};

type GetAssetUpdatedStepConfigParams = {
  asset: Asset;
  componentConfig: UiComponentConfig;
  imageSetting: ComponentSettingImage;
  theme: ThemeAttributes;
};

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
type ImageLocalComponentConfigStyleVariant = ImageLocalComponentConfig['style-variants'][number];
/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
type ImageHostedComponentConfigStyleVariant = ImageHostedComponentConfig['style-variants'][number];

type ComponentSchemaImageLocalTuple = [
  keyof ImageLocalComponentConfigStyleVariant,
  ImageLocalComponentConfigStyleVariant[keyof ImageLocalComponentConfigStyleVariant],
];

/**
 * Converts image component config style variants array based new asset type
 * @returns modified image component config style variants whose shape is determined
 * by the asset type
 */
export const convertImageStyleVariants = (
  params: GetAssetUpdatedStepConfigParams,
): UiImageComponentConfig['style-variants'] => {
  const { asset, componentConfig, theme } = params;

  const componentSchemaBaseImageStyles =
    COMPONENT_SCHEMA[UiComponentConfigType.ImageLocal].config['style-variants']?.[0];

  const generateNewImageHostedStyleVariant = (
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    baseStyleVariant: UiImageComponentConfig['style-variants'][number],
  ): ImageHostedComponentConfigStyleVariant => {
    const imageLocalSpecificStyleKeys: ImageLocalIconStyleKeys[] = [
      'fill-color',
      'fill-color--highlight',
      'fill-color--background',
      'stroke-color',
    ];

    return Object.fromEntries(
      Object.entries(baseStyleVariant).filter(
        ([styleName, _]: ComponentSchemaImageLocalTuple) =>
          !imageLocalSpecificStyleKeys.includes(styleName as ImageLocalIconStyleKeys),
      ),
    ) as ImageHostedComponentConfigStyleVariant;
  };

  const generateNewImageLocalVariant = (
    selectedAssetKey: ImageKey,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    styleVariant: UiImageComponentConfig['style-variants'][number],
  ): ImageLocalComponentConfigStyleVariant => {
    const toHex = (value: string): StyleValueColorConfig => ({ unit: 'hex', value });

    const serializedTheme = serializeTheme({
      id: 'mockTheme',
      type: ApiModel.Theme,
      attributes: theme,
    });

    /**
     * Note(Evan):
     * We need to assign specific color values to icon styles based on derived values
     * from theme icon colors when switcing from image_hosted/image_remote -> image_local.
     * The corresponding style attributes on the component preview don't exist,
     * so the preview attributes will have to be derived from these
     * /

    /**
     * AnimatedCheck is the only exception to the rule, as it doesn't take in its
     * colors from icon theme colors
     * @see front-end/inquiry/components/ImageLocal/ImageLocal.tsx:111
     */
    if ([ImageKey.AnimatedCheck].includes(selectedAssetKey)) {
      return {
        ...styleVariant,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'fill-color': toHex(serializedTheme.colorSuccess),
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'fill-color--highlight': toHex(serializedTheme.colorModalBackground),
        // We return these because they are expected on all ImageLocal components
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'fill-color--background': toHex(serializedTheme.iconColorBackground),
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'stroke-color': toHex(serializedTheme.iconColorStroke),
      };
    }

    /**
     * Option icon assets need to default to a white stroke color when replaced
     */
    if (GOV_ID_OPTION_IMAGE_ASSETS.includes(selectedAssetKey)) {
      return {
        ...styleVariant,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'fill-color': toHex(serializedTheme.iconColorPrimary),
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'stroke-color': toHex(serializedTheme.iconColorHighlight),
        // We return these because they are expected on all ImageLocal components
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'fill-color--highlight': toHex(serializedTheme.colorModalBackground),
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'fill-color--background': toHex(serializedTheme.iconColorBackground),
      };
    }

    if (ImageKey.OptionCheckIcon.includes(selectedAssetKey)) {
      return {
        ...styleVariant,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'fill-color': toHex(serializedTheme.colorSuccess),
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'fill-color--highlight': toHex(serializedTheme.colorModalBackground),
        // We return these because they are expected on all ImageLocal components
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'fill-color--background': toHex(serializedTheme.iconColorBackground),
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'stroke-color': toHex(serializedTheme.iconColorBackground),
      };
    }

    return {
      ...styleVariant,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'fill-color': toHex(serializedTheme.iconColorPrimary),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'fill-color--highlight': toHex(serializedTheme.iconColorHighlight),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'fill-color--background': toHex(serializedTheme.iconColorBackground),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'stroke-color': toHex(serializedTheme.iconColorStroke),
    };
  };

  function throwSentryMessage(asset: Asset) {
    captureMessage(`Unknown asset type when replacing asset`, 'warning', {
      asset,
    });
  }

  if (componentConfig['style-variants'] == null || !componentConfig['style-variants']?.length) {
    // Accounts for image components added before SST support
    switch (asset.type) {
      case AssetType.UPLOADED:
        return [generateNewImageHostedStyleVariant(componentSchemaBaseImageStyles)];
      case AssetType.PERSONA:
        return [generateNewImageLocalVariant(asset.imageKey, componentSchemaBaseImageStyles)];
      default:
        throwSentryMessage(asset);
        return [generateNewImageHostedStyleVariant(componentSchemaBaseImageStyles)];
    }
  } else {
    let updatedImageStyleVariants = componentConfig['style-variants'].slice(0);

    switch (asset.type) {
      case AssetType.UPLOADED:
        if (!isImageHostedComponentConfig(componentConfig)) {
          updatedImageStyleVariants = updatedImageStyleVariants.map(
            generateNewImageHostedStyleVariant,
          );
        }
        break;
      case AssetType.PERSONA:
        updatedImageStyleVariants = updatedImageStyleVariants.map((styleVariant) =>
          generateNewImageLocalVariant(asset.imageKey, styleVariant),
        );
        break;
      default:
        throwSentryMessage(asset);
        break;
    }

    return updatedImageStyleVariants as UiImageComponentConfig['style-variants'];
  }
};

export function getAssetUpdatedImageConfig(params: GetAssetUpdatedStepConfigParams) {
  const { asset, componentConfig, imageSetting } = params;
  const isImageComponent = isImageComponentConfig(componentConfig);
  switch (asset.type) {
    case AssetType.UPLOADED: {
      return {
        ...componentConfig,
        name: componentConfig.name,
        // Only swap component type if its an image component
        ...(isImageComponent && { type: UiComponentConfigType.ImageHosted }),
        config: {
          // remove image key for image components, set it to null otherwise
          ...(isImageComponent
            ? omit(imageSetting['local-image-key'], componentConfig.config)
            : { ...componentConfig.config, [imageSetting['local-image-key']]: null }),
          [imageSetting['client-key']]: asset['blob-id'],
        },
        'style-variants': convertImageStyleVariants(params),
      };
    }
    case AssetType.PERSONA:
      return {
        ...componentConfig,
        name: componentConfig.name,
        // Only swap component type if its an image component
        ...(isImageComponent && { type: UiComponentConfigType.ImageLocal }),
        config: {
          // remove client key (asset id attribute) for image components, set it to null otherwise
          ...(isImageComponent
            ? omit(imageSetting['client-key'], componentConfig.config)
            : componentConfig),
          [imageSetting['local-image-key']]: asset.imageKey,
        },
        'style-variants': convertImageStyleVariants(params),
      };
    default:
      assertNever(asset);
  }
}

export function mapStepsConfigToStepsPreview({
  steps,
  stepsPreview,
  currentLocale,
}: {
  steps: InquiryTemplateVersionStep[];
  stepsPreview: StepPreview[];
  currentLocale: InquiryIso6391LanguageCode;
}): StepPreview[] {
  if (stepsPreview == null || stepsPreview.length === 0) {
    return [];
  }
  const stepsPreviewMap = keyBy('name', stepsPreview);

  const getUpdatedComponentPreviews = (
    componentConfigs: UiComponentConfig[],
    componentPreviews: StepUi,
  ): UiComponent[] => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return componentConfigs
      .map((componentConfig) => {
        let componentPreview = uiStepAllComponents(componentPreviews).find(
          (preview) => componentConfig.name === preview.name,
        );

        // handle coercion of remote images on client for net-new images
        // needs to be ImageLocal as per https://github.com/persona-id/persona-web/issues/24994
        const componentConfigType =
          componentConfig.type === UiComponentConfigType.ImageHosted
            ? UiComponentConfigType.ImageLocal
            : componentConfig.type;

        const screenComponentConfig: ComponentSchemaValues | null =
          isSupportedComponentConfigLibraryType(componentConfigType)
            ? COMPONENT_SCHEMA[componentConfigType]
            : null;

        if (!componentPreview) {
          if (screenComponentConfig != null) {
            componentPreview = {
              name: componentConfig.name,
              ...screenComponentConfig.preview,
            } as UiComponent;
          } else {
            captureMessage(
              `No config found for component ${componentConfig.name} while updating preview`,
              'warning',
            );
            return null;
          }
        }

        // (Note) Evan: This is a safeguard for pre-SST templates and screens
        // added through the UI that do not generate style-variants or styles
        // We'll pull the styles and style-variants from component schema
        // and only fall back to null if it’s unsupported
        if (componentPreview.styles == null && screenComponentConfig != null) {
          componentPreview = {
            ...componentPreview,
            styles: screenComponentConfig.preview.styles,
          } as UiComponent;
        }

        const editorComponentSchema = EDITOR_COMPONENTS_SCHEMA[componentConfig.type];
        let updatedComponentPreview: UiComponent = editorComponentSchema
          ? mergeComponentPreview(
              componentPreview as UiTextComponent,
              getUpdatedComponentText(
                componentConfig as UiTextComponentConfig,
                componentPreview as UiTextComponent,
                editorComponentSchema as EditorComponentSchema<UiTextComponentConfig>,
                currentLocale,
              ),
            )
          : componentPreview;

        if (
          isUiParentComponentConfig(componentConfig) &&
          isUiParentComponent(updatedComponentPreview)
        ) {
          updatedComponentPreview = mergeComponentPreview(
            updatedComponentPreview as UiParentComponent,
            {
              ...(componentConfig.type === UiComponentConfigType.Footer && {
                direction: componentConfig.config.direction,
              }),
              children: getUpdatedComponentPreviews(
                (componentConfig as UiParentComponentConfig).config.children,
                componentPreviews,
              ),
            },
          );
        }

        const componentStyleVariantsSource =
          componentConfig['style-variants'] ??
          (screenComponentConfig?.config?.['style-variants'] || null);

        return STYLEABLE_COMPONENTS.includes(componentConfig.type)
          ? createStyledComponentPreview(updatedComponentPreview, componentStyleVariantsSource)
          : updatedComponentPreview;
      })
      .filter(Boolean);
  };

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return steps
    .map((step: InquiryTemplateVersionStep) => {
      const stepPreview = stepsPreviewMap[step.name];
      if (!stepPreview) {
        return null;
      }

      switch (true) {
        case isUiStepConfig(step) && isUiStep(stepPreview):
          return produce(stepPreview, (draftPreview) => {
            draftPreview.config.components = getUpdatedComponentPreviews(
              step.config.components,
              draftPreview,
            );
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            draftPreview.styles = mergeStyles(draftPreview.styles, step['style-variants']);
          });
        case isCombinedStepConfig(step) && isCombinedStep(stepPreview): {
          const { localizations: configLocalizations } = step.config;
          const { localizations: previewLocalizations } = stepPreview.config;
          return mergeCombinedStepPreview(
            stepPreview,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            Object.keys(configLocalizations ?? {}).reduce(
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              (attrs, key: TranslationKeys<typeof configLocalizations>) => ({
                ...attrs,
                [key]: getUpdatedLocalizationText(
                  configLocalizations[key],
                  previewLocalizations[key],
                  currentLocale,
                ),
              }),
              previewLocalizations,
            ),
          );
        }
        default:
          return stepPreview;
      }
    })
    .filter(Boolean);
}

export const generateUpdatedStepFromComponentUpdates = (
  step: InquiryTemplateVersionStepUi,
  componentPreviewPath: string[],
  componentConfigUpdates: Record<string, any>,
) => {
  const componentPath = componentPreviewPath.join('.');
  return produce(step, (draftStep: InquiryTemplateVersionStepUi) => {
    const draftComponent = get(`config.components.${componentPath}`, draftStep);

    if (draftComponent != null && draftComponent.config != null) {
      Object.keys(componentConfigUpdates).forEach((attributePath) => {
        if (has(attributePath, draftComponent.config)) {
          draftComponent.config = set(
            attributePath,
            componentConfigUpdates[attributePath],
            draftComponent.config,
          );
        }
      });
    }
  });
};
